/**
 * InboxPage
 *
 * Web port of Flutter's `MessagesScreen`
 * (`lib/presentation/screens/messages/messages_screen.dart`). Renders the unified
 * notifications + direct-messages feed produced by `useInbox` through a single
 * `VirtualList`, reproducing the Flutter sliver order
 * (`messages_screen.dart:704-768`): Updates header → notifications →
 * "More updates" row → Direct messages header → chat rows.
 *
 * Page+hook split (per `presentation.md`): this page owns the `screenKey` mint,
 * the Redux `currentUser` gate, `useBasePage` (for keep-alive / React-Query cache
 * behaviour), and every navigation / sheet invocation the feature hook cannot
 * make — `Navigator.navigateToChat` for chat-row taps, plus the wiring of the
 * three notification tap handlers (`messages_screen.dart:722-724`).
 *
 * Notification taps. The three tap handlers live in the colocated surface hook
 * `notificationTap/useNotificationTapHandlers.ts`; the page only mints their
 * inputs (`screenKey`, `l10n`) and threads `markNotificationRead` /
 * `refreshCurrentUser` through from `useInboxContext()`. Dispatch-then-mark
 * ordering, the Flutter citations and the Clean-Architecture rationale are
 * documented there and in `notificationTap/notificationTapRouter.ts`. The page
 * itself invokes no use case and holds no `read` predicate.
 *
 * Shared inbox state (Option 3, finding 1): the page consumes the ONE shared
 * `useInbox` core through `useInboxContext` (provided by `InboxProvider`, mounted
 * in `ProtectedRoutesWrapper` above the route cache) rather than mounting its own
 * `useInbox`. The core was
 * already initialized at app startup, so opening `/inbox` renders the
 * already-fetched state and does NOT re-fire the first-page fetches. The page
 * therefore wires NO `onInit`/`onDispose` lifecycle: the startup fetch and the
 * app-lifetime chat subscription are owned by the always-mounted core, and the
 * subscription must NOT be torn down on `/inbox` exit (that would re-break the
 * live badge).
 *
 * Loading gate reproduces Flutter's COMPOUND predicate exactly
 * (`messages_screen.dart:695`): the centered spinner shows only while
 * `isFetchingChatNodes && isFirstLoad` — NOT `isFirstLoad` alone. A separate
 * `currentUser === null` auth guard renders nothing (web-only concern).
 *
 * UI deviations (documented per `presentation.md`):
 *   - No `MessagesAppBar` port — the web Inbox tab lives in the TopBar
 *     (`HomeTopBarHeader`), so the page renders no in-list app bar.
 *   - No `didChangeAppLifecycleState` equivalent (it is empty in Flutter;
 *     task-prompt point 5).
 *   - One `VirtualList` over a discriminated-union item model rather than
 *     Flutter's separate sliver delegates (web pattern, `inboxItem.ts`).
 */
import { useCallback, useRef } from 'react';
import { useSelector } from 'react-redux';
import { VirtualList } from '@/presentation/components/common/virtualList/VirtualList';
import { ExpauseLoadingIndicator } from '@/presentation/components/common/loadingIndicators/ExpauseLoadingIndicator';
import { useTheme } from '@/presentation/contexts/ThemeContext';
import { useLocale } from '@/presentation/contexts/LocaleContext';
import { useResponsiveDesign } from '@/presentation/responsiveDesign/responsiveDesign';
import { useBasePage } from '@/presentation/hooks/useBasePage';
import { useKeepAlive } from '@/presentation/routing/RouteStack';
import { generateScreenKey } from '@/presentation/routing/screenKeyUtils';
import { Navigator } from '@/presentation/routing/Navigator';
import { qaAttr } from '@/presentation/utils/qaAttr';
import type { RootState } from '@/presentation/store';
import type { ChatMessageNodeEntity } from '@/domain/entities/chat/chatMessageNodeEntity';
import type { ChatUsersInfoEntity } from '@/domain/entities/info/chatUsersInfoEntity';
import type { InboxItem } from './types/inboxItem';
import { useInboxContext } from '@/presentation/contexts/InboxContext';
import { useNotificationTapHandlers } from './notificationTap/useNotificationTapHandlers';
import { InboxItemRow, getInboxItemKey } from './components/InboxItemRow';

export const InboxPage = () => {
  const { theme } = useTheme();
  const { l10n } = useLocale();
  const responsive = useResponsiveDesign();

  // Auth gate (page-owned per `presentation.md`). The hook reads the user too
  // for its own business logic; the page reads it for the chat-tap struct
  // (`currentName`) and the `currentUserId` the routed rows need.
  const currentUser = useSelector((state: RootState) => state.auth.user);
  const currentUserId = currentUser?.firebaseId ?? '';

  // Canonical screen-key wiring (mirrors `FeedPage` / `ContactPage`).
  const { currentScreenKeyRef } = useKeepAlive();
  const screenKeyRef = useRef<string>(
    currentScreenKeyRef.current || generateScreenKey(location.pathname),
  );
  const screenKey = screenKeyRef.current;

  const {
    items,
    isFirstLoad,
    isFetchingChatNodes,
    isEmpty,
    getNotifications,
    collapseNotifications,
    loadMoreChatNodes,
    markNotificationRead,
    refreshCurrentUser,
  } = useInboxContext();

  // Keep-alive / React-Query cache wiring only — the startup fetch and the chat
  // subscription teardown are owned by the shared core (`InboxProvider`), NOT by
  // this page, so no `onInit`/`onDispose` here (finding 1).
  useBasePage({ screenKey });

  // The three notification tap handlers live in the colocated surface hook so
  // this page stays under the component-size threshold; the page supplies the
  // values the hook cannot mint itself.
  const { onNotificationItemTap, onNotificationUserTap, onNotificationMediaTap } =
    useNotificationTapHandlers({ screenKey, l10n, markNotificationRead, refreshCurrentUser });

  // Chat-row tap → build `ChatUsersInfoEntity` exactly as Flutter
  // `_goToChatScreen` (`messages_screen.dart:615-627`), then navigate.
  const handleChatTap = useCallback(
    (node: ChatMessageNodeEntity) => {
      const isCurrentUserSender = currentUserId === node.idFrom;
      const chatUsersInfo: ChatUsersInfoEntity = {
        currentFirebaseId: currentUserId,
        peerId: isCurrentUserSender ? node.idTo : node.idFrom,
        currentName: currentUser?.name ?? '',
        peerName: isCurrentUserSender ? node.nameUser2 : node.nameUser1,
        createdTimestamp: node.createdTimestamp,
        groupChatId: node.chatId,
      };
      Navigator.navigateToChat(chatUsersInfo);
    },
    [currentUserId, currentUser?.name],
  );

  // Compound first-load gate — reproduces `messages_screen.dart:695`
  // (`_isFetchingChatItems && _isFirstLoad`). BOTH conjuncts are required.
  const isInitialLoading = isFetchingChatNodes && isFirstLoad;

  // Coarse lifecycle token for the qa-tester (mirrors `WalletPage`'s
  // `walletStatus`), derived from the same predicates the render uses.
  const inboxStatus = isInitialLoading ? 'loading' : isEmpty ? 'empty' : 'ready';

  // Thin per-row delegate — the per-variant `switch` (and its parallel
  // `getInboxItemKey`) lives in the colocated `InboxItemRow` subcomponent.
  const renderItem = useCallback(
    (item: InboxItem) => (
      <InboxItemRow
        item={item}
        currentUserId={currentUserId}
        onNotificationItemTap={onNotificationItemTap}
        onNotificationUserTap={onNotificationUserTap}
        onNotificationMediaTap={onNotificationMediaTap}
        onMore={getNotifications}
        onCollapse={collapseNotifications}
        onChatTap={handleChatTap}
      />
    ),
    [
      currentUserId,
      onNotificationItemTap,
      onNotificationUserTap,
      onNotificationMediaTap,
      getNotifications,
      collapseNotifications,
      handleChatTap,
    ],
  );

  // Auth guard — separate web concern (the hook can't render). Render nothing
  // until the user is resolved; the spinner-vs-list decision below is the
  // Flutter `&&` of the two chat flags.
  if (currentUser === null) {
    return <div {...qaAttr({ id: 'inbox', status: 'loading' })} />;
  }

  return (
    <div {...qaAttr({ id: 'inbox', status: inboxStatus })}>
      {isInitialLoading ? (
        <div
          className="flex justify-center"
          style={{ padding: `${responsive.spacing['3xl']}px 0` }}
        >
          <ExpauseLoadingIndicator />
        </div>
      ) : isEmpty ? (
        <div
          className="text-center"
          style={{ padding: `${responsive.spacing['3xl']}px 0` }}
        >
          <p style={{ color: theme.text.secondary }}>{l10n.inbox.emptyPlaceholder}</p>
        </div>
      ) : (
        <>
          <VirtualList<InboxItem>
            qa={{ id: 'inbox-list' }}
            items={items}
            renderItem={renderItem}
            getItemKey={getInboxItemKey}
            gap={0}
            onReachedEnd={loadMoreChatNodes}
            reachedEndThreshold={400}
          />
          {/* Trailing pagination spinner — mirrors Flutter's
              `Visibility(visible: _isFetchingChatItems)` sliver
              (`messages_screen.dart:795-805`). Shown while a non-first-load
              chat fetch is in flight; gated on `!isInitialLoading` so it never
              double-renders with the centered first-load spinner above. */}
          {isFetchingChatNodes && !isInitialLoading && (
            <div
              className="flex justify-center"
              style={{ padding: `${responsive.spacing.sm}px 0` }}
              {...qaAttr({ id: 'inbox-pagination-loading', status: 'loading' })}
            >
              <ExpauseLoadingIndicator />
            </div>
          )}
        </>
      )}
    </div>
  );
};
